import { useMyOrganizations, type Organization } from "@orkestor/core";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/** The active producing organization (productora). The producer console scopes
    its offers to it; the top-bar selector switches it. Persisted per-browser so
    the choice survives reloads. */
interface OrgContextValue {
  orgs: Organization[];
  activeOrg: Organization | null;
  setActiveOrgId: (id: string) => void;
  isLoading: boolean;
}

const OrgContext = createContext<OrgContextValue | null>(null);
const STORAGE_KEY = "orkestor.activeOrgId";

export function OrgProvider({ children }: { children: ReactNode }) {
  const query = useMyOrganizations();
  const orgs = useMemo(() => query.data ?? [], [query.data]);
  const [activeId, setActiveId] = useState<string | null>(
    () => window.localStorage.getItem(STORAGE_KEY),
  );

  // Default to the first org once loaded (or when the stored id is gone).
  useEffect(() => {
    if (orgs.length === 0) return;
    const known = activeId && orgs.some((o) => o.id === activeId);
    if (!known) setActiveId(orgs[0]!.id);
  }, [orgs, activeId]);

  const setActiveOrgId = useCallback((id: string) => {
    setActiveId(id);
    window.localStorage.setItem(STORAGE_KEY, id);
  }, []);

  const value = useMemo<OrgContextValue>(
    () => ({
      orgs,
      activeOrg: orgs.find((o) => o.id === activeId) ?? orgs[0] ?? null,
      setActiveOrgId,
      isLoading: query.isPending,
    }),
    [orgs, activeId, setActiveOrgId, query.isPending],
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useActiveOrg(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useActiveOrg must be used within <OrgProvider>");
  return ctx;
}
